'use client';

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Download, Loader2, Plus, AlertCircle, Upload, ListChecks, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StandardButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { GenericSelect } from '@/styles/GenericForm';
import { GenericConfirmDialog } from '@/styles/GenericConfirmDialog';
import { useProject } from '@/components/ProjectSelector';
import { useUser } from '@/contexts/UserContext';
import { fetchEntityList, writeEntity } from '@/lib/entity-api';
import { downloadCsv } from '@/lib/csv';
import { costCodeLabel, createCostCode, defaultCostCodeOf, useCostCodes, type CostCode } from '@/lib/cost-codes';
import { lineCostCodeProblem } from '@/lib/line-items';
import { trackFormSubmission } from '@/lib/posthog-events';
import { fetchBudgetFinancials, type BudgetFinancials } from '../services/budget-api';
import {
  budgetTotal,
  budgetStatusOf,
  BUDGET_STATUSES,
  buildBudgetView,
  isBlankLine,
  newLine,
  overallMarkup,
  roundCents,
  toBudgetLine,
  toPricingOverrides,
  toStoredItem,
  type BudgetStatus,
  type PricingOverrides,
  type RowPricing,
  type BudgetCodeBlock,
  type BudgetFigures,
  type BudgetLine,
  type BudgetRow,
} from '../lib/budget-lines';
import { useAutosave } from '../hooks/useAutosave';
import { CostCodeCombobox } from './cost-code-combobox';

const formatMoney = (amount: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

// A 20px input in a py-1 cell = the 28px height of the calculated rows (py-1 text cells); px-1 + px-2 = their px-3, so the text lines up.
const CELL_INPUT = 'w-full h-5 bg-transparent border-0 shadow-none px-2 text-sm leading-5 focus:outline-none focus:bg-white focus:ring-1 focus:ring-blue-500 rounded';
const INPUT_CELL = 'py-1 px-1';
// the header cells: the same token as every table, stuck to the top of the scrolling box
const HEAD = 'sticky top-0 z-10 bg-table-header py-1 text-sm';
const ORDERS_TITLE = 'POs / Bills / Change Orders / Selections / Material Orders';
const rowTitle = (kind: BudgetRow['kind']) => (kind === 'orders' ? ORDERS_TITLE : 'Bills (no PO)');
const NOT_APPLICABLE = <span className="text-gray-300" title="Not applicable: this line adds up the purchase orders and bills of the cost code">—</span>;

/**
 * A number the user types: the WHOLE value is selected when the cell is entered (so typing replaces it), free text while the cell has focus,
 * committed (and formatted) on blur. Used by Qty, Unit Cost, Markup, Client Pricing, Tax and Other Amount.
 * `value` null = nothing to show (a derived percentage without a cost); an emptied cell calls `onClear` when there is one (else commits 0).
 */
function NumberCell({ value, onCommit, onClear, label, money = false, decimals }: { value: number | null; onCommit: (value: number) => void; onClear?: () => void; label: string; money?: boolean; decimals?: number }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const justEntered = useRef(false);
  const fixed = decimals ?? (money ? 2 : undefined);
  const format = (n: number) => (fixed === undefined ? String(n) : n.toFixed(fixed));
  const shown = draft ?? (value === null ? '' : format(value));
  // The text changes when the cell is entered (formatted "0.00" -> raw "0"): the selection is made AFTER that change, or it would select the old text
  useLayoutEffect(() => {
    if (draft !== null && justEntered.current) {
      justEntered.current = false;
      inputRef.current?.select();
    }
  }, [draft]);
  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      aria-label={label}
      value={shown}
      placeholder={value === null ? '—' : undefined}
      onFocus={() => { justEntered.current = true; setDraft(value === null ? '' : String(value)); }}
      onChange={(event) => {
        const text = event.target.value;
        if (!/^-?\d*\.?\d*$/.test(text)) return;
        setDraft(text);
        const parsed = parseFloat(text);
        if (text.trim() === '' && onClear) onClear();
        else onCommit(Number.isFinite(parsed) ? parsed : 0); // the totals follow every keystroke; the cell is tidied on blur
      }}
      onBlur={() => setDraft(null)}
      className={`${CELL_INPUT} text-right`}
    />
  );
}

/** Everything the user edits on the Budget, saved together by the one autosave. */
interface BudgetDoc {
  lines: BudgetLine[];
  /** Markup / Client Pricing of the calculated rows, by row id */
  overrides: PricingOverrides;
  /** the budget-level Tax (% of the lines) and Other Amount */
  taxPct: number;
  otherAmount: number;
  /** the Client Pricing column's own Tax (%) and Other Amount */
  clientTaxPct: number;
  clientOtherAmount: number;
  status: BudgetStatus;
  /** the default Markup (%) of NEW lines; null = none. Applying it to the existing lines is a separate, explicit action */
  generalMarkup: number | null;
}

interface Snapshot extends BudgetDoc {
  projectId: string;
}

const EMPTY_DOC: BudgetDoc = { lines: [], overrides: {}, taxPct: 0, otherAmount: 0, clientTaxPct: 0, clientOtherAmount: 0, status: 'draft', generalMarkup: null };

const money = (value: number) => formatMoney(value);
const negative = (value: number) => (value < 0 ? 'text-red-600 font-medium' : '');

/** A new line starts with the company's default cost code and, when there is one, the General Markup as its Markup (%); with none, no markup. */
const freshLine = (generalMarkup: number | null, defaultCode?: Pick<CostCode, 'id' | 'code'>): BudgetLine =>
  newLine({
    ...(defaultCode ? { costCodeId: defaultCode.id, costCode: defaultCode.code } : {}), // every line starts on the company's default cost code (never invented: none = the user must pick)
    ...(generalMarkup === null ? {} : { pricing: { source: 'markup' as const, markupPct: generalMarkup } }),
  });

/**
 * THE budget of the selected project. For every cost code: the user's MANUAL lines (Cost Code, Item, Qty, Unit, Unit Cost; Budget = Qty x Unit Cost; saved
 * by itself, useAutosave) and, computed from the project's purchase orders and bills, ONE "POs / Bills" line (Budget 0, Committed = the total value of
 * the orders, Billed = what their bills billed) and, if there are bills with no order, one "Bills (no PO)" line. Lines of one cost code sit together, under
 * the company's cost code group when it has one. The project is always the global project selector's.
 */
export function BudgetTable() {
  const { selectedProject, projects } = useProject();
  const { companyId } = useUser();
  const projectId = selectedProject && selectedProject !== 'all' ? selectedProject : '';
  const projectName = projects.find((p) => p.id === projectId)?.name;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [doc, setDoc] = useState<BudgetDoc>(EMPTY_DOC);
  const { lines, overrides, taxPct, otherAmount, clientTaxPct, clientOtherAmount, status, generalMarkup } = doc;
  const [financials, setFinancials] = useState<BudgetFinancials | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [collapsedSections, setCollapsedSections] = useState<string[]>([]); // categories start expanded
  const docRef = useRef<BudgetDoc>(EMPTY_DOC); // the latest document, for the next edit (never stale between two quick keystrokes, and one field never saves a stale copy of another)
  const budgetIdRef = useRef<string | null>(null); // the project's budget row, once it exists
  const versionRef = useRef<number | null>(null); // the version of that row this page last read / saved: every save carries it, the server refuses a stale one
  const [conflictOpen, setConflictOpen] = useState(false); // another tab / user saved a newer version
  const [reloadTick, setReloadTick] = useState(0);
  const { codes, add: addCostCode } = useCostCodes(!!projectId);
  const defaultCode = defaultCostCodeOf(codes);
  const defaultId = defaultCode?.id ?? '';
  const defaultCodeRef = useRef<CostCode | undefined>(undefined);
  defaultCodeRef.current = defaultCode;
  const legacyUncodedRef = useRef<Set<string>>(new Set()); // lines the budget ALREADY had without a cost code (history): left as they are, never forced
  const defaultIdRef = useRef('');
  defaultIdRef.current = defaultId;
  const isBlank = (line: BudgetLine) => isBlankLine(line, defaultId);
  // A new line the user has not touched yet gets the default cost code as soon as the company's codes are known (the starter line is made before they load).
  // This is not an edit: it saves nothing by itself.
  useEffect(() => {
    if (!defaultCode) return;
    const untouched = (line: BudgetLine) => !line.costCodeId && !line.description.trim() && line.unitCost === 0;
    const current = docRef.current;
    if (!current.lines.some(untouched)) return;
    const next = { ...current, lines: current.lines.map((line) => (untouched(line) ? { ...line, costCodeId: defaultCode.id, costCode: defaultCode.code } : line)) };
    docRef.current = next;
    setDoc(next);
  }, [defaultCode, doc.lines]);
  const createAndRemember = async (text: string) => {
    // a code typed here is a Child of 0000 · Unassigned until it is classified in Catalog > Cost Codes
    const created = await createCostCode(text, codes.find((c) => c.default_key)?.parent_id ?? null);
    addCostCode(created);
    return created;
  };

  const persist = useCallback(async (snapshot: Snapshot) => {
    const kept = snapshot.lines.filter((line) => !isBlankLine(line, defaultIdRef.current));
    // every line has a cost code: nothing is saved while one has none (the same generic message every item table shows)
    const missingCode = lineCostCodeProblem(kept.filter((line) => !legacyUncodedRef.current.has(line.id)));
    if (missingCode) throw new Error(missingCode);
    const items = kept.map(toStoredItem);
    const subtotal = budgetTotal(kept);
    const body = {
      items,
      // Budget = the lines + the budget-level Tax (% of the lines) + Other Amount: the document total every reader of the budget shows
      total_amount: Math.round((subtotal + subtotal * (snapshot.taxPct / 100) + snapshot.otherAmount) * 100) / 100, // the budget document's own total (lines + Budget Tax + Other)
      // this table has no profit / overhead (Tax is the budget-level Tax)
      profit_percentage: 0,
      tax_percentage: snapshot.taxPct,
      overhead_percentage: 0,
      other_amount: snapshot.otherAmount,
      pricing_overrides: snapshot.overrides,
      client_tax_percentage: snapshot.clientTaxPct,
      client_other_amount: snapshot.clientOtherAmount,
      status: snapshot.status,
      general_markup: snapshot.generalMarkup,
    };
    try {
      if (budgetIdRef.current) {
        const saved = await writeEntity<{ version?: number }>(`api/entities/budget/${budgetIdRef.current}`, 'PUT', { ...body, expected_version: versionRef.current ?? undefined }, 'Failed to save the budget');
        versionRef.current = saved?.version ?? versionRef.current; // the next autosave of THIS page carries the version this save produced: no false conflicts
      } else {
        try {
          const created = await writeEntity<{ id: string; version?: number }>('api/entities/budget', 'POST', { project_id: snapshot.projectId, ...body }, 'Failed to save the budget');
          budgetIdRef.current = created.id;
          versionRef.current = created.version ?? null;
        } catch (failure) {
          // somebody (or another tab) created this project's budget meanwhile: that is a newer version than ours -- never write over it
          const existing = (await fetchEntityList<any>('budget', { projectId: snapshot.projectId }))[0];
          if (!existing) throw failure;
          budgetIdRef.current = existing.id;
          versionRef.current = existing.version ?? null;
          throw Object.assign(new Error('Another tab or user already created this budget. Reload it before saving again.'), { code: 'version_conflict' });
        }
      }
      trackFormSubmission({ form_type: 'budget_autosave', success: true });
    } catch (failure) {
      trackFormSubmission({ form_type: 'budget_autosave', success: false, error_message: failure instanceof Error ? failure.message : 'Failed to save the budget' });
      if ((failure as { code?: string }).code === 'version_conflict') setConflictOpen(true);
      throw failure;
    }
  }, []);
  const autosave = useAutosave<Snapshot>(persist);

  // -- load the selected project's budget (its manual lines), what is committed / billed, and the company's cost code groups
  useEffect(() => {
    let cancelled = false;
    budgetIdRef.current = null;
    versionRef.current = null;
    legacyUncodedRef.current = new Set();
    docRef.current = EMPTY_DOC;
    setDoc(EMPTY_DOC);
    setFinancials(null);
    setLoadError(null);
    setSelecting(false);
    setSelected([]);
    setExpanded([]);
    setConflictOpen(false);
    if (!projectId || !companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const [budgets, money] = await Promise.all([
          fetchEntityList<any>('budget', { projectId }),
          fetchBudgetFinancials(projectId).catch((failure) => { console.error('Error loading committed / billed:', failure); return null; }),
        ]);
        if (cancelled) return;
        const budget = budgets[0] ?? null;
        budgetIdRef.current = budget ? budget.id : null;
        versionRef.current = budget && Number.isInteger(budget.version) ? budget.version : null;
        const stored = Array.isArray(budget?.items) ? (budget.items as any[]).map(toBudgetLine) : [];
        // a project that already has purchase orders needs no empty starter row: its cost codes are listed (Add Line is always there)
        const hasCodes = !!money && (Object.keys(money.by_cost_code ?? {}).length > 0 || (money.source_codes?.length ?? 0) > 0);
        const storedMarkup = budget?.general_markup === null || budget?.general_markup === undefined ? null : Number(budget.general_markup);
        const generalMarkup = storedMarkup !== null && Number.isFinite(storedMarkup) ? storedMarkup : null;
        legacyUncodedRef.current = new Set(stored.filter((line) => !line.costCodeId).map((line) => line.id));
        const initial = stored.length > 0 ? stored : (hasCodes ? [] : [freshLine(generalMarkup, defaultCodeRef.current)]);
        const loaded: BudgetDoc = {
          lines: initial,
          overrides: toPricingOverrides(budget?.pricing_overrides),
          taxPct: Math.max(Number(budget?.tax_percentage) || 0, 0),
          otherAmount: Math.max(Number(budget?.other_amount) || 0, 0),
          clientTaxPct: Math.min(Math.max(Number(budget?.client_tax_percentage) || 0, 0), 100),
          clientOtherAmount: Math.max(Number(budget?.client_other_amount) || 0, 0),
          status: budgetStatusOf(budget?.status),
          generalMarkup,
        };
        docRef.current = loaded;
        setDoc(loaded);
        setFinancials(money);
      } catch (failure) {
        console.error('Error loading the budget:', failure);
        if (!cancelled) setLoadError(failure instanceof Error ? failure.message : 'Failed to load the budget');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, companyId, reloadTick]);

  // every edit: show it at once, save it soon, with the latest state of EVERYTHING (lines, pricing, charges): the autosave keeps only the newest snapshot
  const editDoc = (update: (current: BudgetDoc) => BudgetDoc) => {
    const next = update(docRef.current);
    docRef.current = next;
    setDoc(next);
    autosave.schedule({ projectId, ...next });
  };
  const edit = (update: (current: BudgetLine[]) => BudgetLine[]) => editDoc((current) => ({ ...current, lines: update(current.lines) }));
  const changeLine = (id: string, patch: Partial<BudgetLine>) => edit((current) => current.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  // Markup or Client Pricing typed on a row: that value becomes the row's source of truth (undefined = back to no markup)
  const setPricing = (row: BudgetRow, pricing: RowPricing | undefined) => {
    if (row.kind === 'manual') { changeLine(row.id, { pricing }); return; }
    editDoc((current) => {
      const overrides = { ...current.overrides };
      if (pricing) overrides[row.id] = pricing; else delete overrides[row.id];
      return { ...current, overrides };
    });
  };
  // General Markup: typing it only sets the default of NEW lines. This button is the explicit bulk action: every line that can be priced (the manual lines
  // with content, and the POs / Bills rows with a cost) gets that Markup now. Afterwards each line is edited on its own; nothing re-applies it.
  const applyGeneralMarkup = () => {
    if (generalMarkup === null) return;
    const pricing: RowPricing = { source: 'markup', markupPct: generalMarkup };
    const calculated = allRows.filter((row) => row.kind !== 'manual' && row.basis > 0).map((row) => row.id);
    editDoc((current) => {
      const next = { ...current.overrides };
      calculated.forEach((id) => { next[id] = pricing; });
      return { ...current, lines: current.lines.map((line) => (isBlank(line) ? line : { ...line, pricing })), overrides: next };
    });
  };
  // a new line goes to the END of the stored lines (and shows at the end of the table until it has a cost code)
  const addLine = () => edit((current) => [...current, freshLine(docRef.current.generalMarkup, defaultCode)]);

  const view = useMemo(() => buildBudgetView(lines, financials, codes, overrides, { taxPct, otherAmount, clientTaxPct, clientOtherAmount }), [lines, financials, codes, overrides, taxPct, otherAmount, clientTaxPct, clientOtherAmount]);
  const { totals } = view;
  // the Budget column added up: the manual lines + the change orders' items (the rows that carry them). Tax is still a % of the manual lines.
  const columnBudget = roundCents(totals.subtotal + totals.changeOrders + totals.selections + totals.materialOrders);
  const hasGroups = view.sections.some((section) => section.label !== null);
  const manualRows: BudgetRow[] = useMemo(() => [...view.sections.flatMap((s) => s.blocks.flatMap((b) => b.rows)), ...view.noCodeRows].filter((r) => r.kind === 'manual'), [view]);
  const allRows: BudgetRow[] = useMemo(() => [...view.sections.flatMap((s) => s.blocks.flatMap((b) => b.rows)), ...view.noCodeRows], [view]);
  const codeText = (row: BudgetRow) => {
    const code = codes.find((c) => c.id === row.costCodeId);
    return code ? costCodeLabel(code) : (row.line?.costCode || '');
  };

  // -- selection (the menu's Select): only manual lines can be selected; the consolidated ones are calculated from other documents
  const selectableIds = manualRows.map((r) => r.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.includes(id));
  const someSelected = selected.length > 0 && !allSelected;
  const headerCheckboxRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = someSelected; }, [someSelected]);
  const toggleAll = (checked: boolean) => setSelected(checked ? selectableIds : []);
  const toggleRow = (id: string, checked: boolean) => setSelected((current) => (checked ? Array.from(new Set([...current, id])) : current.filter((value) => value !== id)));
  const leaveSelection = () => { setSelecting(false); setSelected([]); };
  const deleteSelected = () => {
    if (!selecting || selected.length === 0) {
      alert('Select at least one line to delete (use Select in the menu).');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selected.length} line(s)? This action cannot be undone.`)) return;
    const doomed = new Set(selected);
    edit((current) => {
      const next = current.filter((line) => !doomed.has(line.id));
      return next.length > 0 || allRows.some((r) => r.kind !== 'manual') ? next : [freshLine(docRef.current.generalMarkup, defaultCode)];
    });
    leaveSelection();
  };

  const toggleSection = (key: string) => setCollapsedSections((current) => (current.includes(key) ? current.filter((value) => value !== key) : [...current, key]));
  const toggleExpanded = (id: string) => setExpanded((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

  const exportLines = () => {
    const rows = allRows
      .filter((row) => row.kind !== 'manual' || (row.line && !isBlank(row.line)))
      .map((row) => ({
        cost_code: codeText(row),
        item: row.kind === 'manual' ? row.line?.description ?? '' : rowTitle(row.kind),
        qty: row.line ? row.line.quantity : '',
        unit: row.line ? row.line.unit : '',
        unit_cost: row.line ? row.line.unitCost : '',
        budget: row.budget,
        committed: row.committed,
        billed: row.billed,
        remaining: row.remaining,
      }));
    if (rows.length === 0) { alert('No data found to export.'); return; }
    downloadCsv(`budget-${(projectName || 'project').replace(/[^\w-]+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`, rows);
  };

  if (!projectId) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-600">
          Select a project in the project selector to see and edit its Budget. The Overview tab lists every project.
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...
      </div>
    );
  }

  if (loadError) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-red-700">Could not load the budget: {loadError}</CardContent>
      </Card>
    );
  }

  const tile = (title: string, value: number, hint?: React.ReactNode, tone?: 'negative') => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${tone === 'negative' ? 'text-red-600' : 'text-black'}`}>{formatMoney(value)}</div>
        {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );

  const saveBadge = (() => {
    switch (autosave.status) {
      case 'saving': return <span className="flex items-center text-sm text-gray-500"><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving...</span>;
      case 'saved': return <span className="flex items-center text-sm text-green-700"><Check className="w-4 h-4 mr-1" /> All changes saved</span>;
      case 'unsaved': return <span className="text-sm text-gray-500">Unsaved changes...</span>;
      case 'error': return (
        <span className="flex items-center gap-2 text-sm text-red-700" role="alert">
          <AlertCircle className="w-4 h-4" /> Could not save{autosave.error ? `: ${autosave.error}` : ''}. Your changes are kept.
          <button type="button" className="underline font-medium" onClick={() => void autosave.retry()}>Retry</button>
        </span>
      );
      default: return <span className="text-sm text-gray-400">Changes save automatically</span>;
    }
  })();

  const cell = 'py-0.5 px-3 text-right text-sm whitespace-nowrap'; // amounts never wrap: separators and decimals stay whole
  const selectCell = (row?: BudgetRow) => selecting && (
    <TableCell className="py-0.5 px-2 w-8 text-center">
      {row && row.kind === 'manual' ? (
        <Checkbox aria-label={`Select ${row.line?.description || 'line'}`} checked={selected.includes(row.id)} className="h-3.5 w-3.5" onCheckedChange={(value) => toggleRow(row.id, Boolean(value))} />
      ) : row ? (
        <span className="text-gray-300" title="Calculated from purchase orders and bills: it cannot be selected or deleted here">—</span>
      ) : null}
    </TableCell>
  );

  const figuresCells = (figures: Pick<BudgetFigures, 'budget' | 'committed' | 'billed' | 'remaining'>, testId?: string, bold = false) => (
    <>
      <TableCell className={`${cell} ${bold ? 'font-bold' : 'font-medium'}`} data-testid={testId}>{money(figures.budget)}</TableCell>
      <TableCell className={`${cell} ${bold ? 'font-bold' : ''}`}>{money(figures.committed)}</TableCell>
      <TableCell className={`${cell} ${bold ? 'font-bold' : ''}`}>{money(figures.billed)}</TableCell>
      <TableCell className={`${cell} ${bold ? 'font-bold' : ''} ${negative(figures.remaining)}`}>{money(figures.remaining)}</TableCell>
    </>
  );

  // Markup (%) and Client Pricing of a row you can price: type either one, the last one typed is kept and the other follows. Emptying a cell removes the markup.
  const pricingCells = (row: BudgetRow) => (
    <>
      <TableCell className={INPUT_CELL}>
        <NumberCell label="Markup (%)" decimals={2} value={row.markupPct} onCommit={(markupPct) => setPricing(row, { source: 'markup', markupPct })} onClear={() => setPricing(row, undefined)} />
      </TableCell>
      <TableCell className={INPUT_CELL}>
        {/* typing a Client Price sets the part the markup works on: the change orders' Client Price (on the default code) is added to it, never typed over */}
        <NumberCell label="Client Price" money value={row.clientPrice} onCommit={(clientPrice) => setPricing(row, { source: 'client', clientPrice: Math.max(0, clientPrice - (row.linkedClient ?? 0)) })} onClear={() => setPricing(row, undefined)} />
      </TableCell>
    </>
  );
  // A parent row or a total only adds up the rows' Client Pricing (each row once): it is shown, never edited
  const aggregatePricingCells = (figures: Pick<BudgetFigures, 'basis' | 'clientPrice'>, bold = false) => {
    const markup = overallMarkup(figures.basis, figures.clientPrice);
    return (
      <>
        <TableCell className={`${cell} ${bold ? 'font-bold' : ''}`}>{markup === null ? '—' : markup.toFixed(2)}</TableCell>
        <TableCell className={`${cell} ${bold ? 'font-bold' : 'font-medium'}`}>{money(figures.clientPrice)}</TableCell>
      </>
    );
  };

  const manualRow = (row: BudgetRow) => {
    const line = row.line as BudgetLine;
    return (
      <TableRow key={row.id} className="group bg-blue-50/60" data-testid="row-manual">
        {selectCell(row)}
        <TableCell className={INPUT_CELL}>
          <CostCodeCombobox
            value={line.costCodeId}
            fallbackLabel={line.costCode}
            catalog={codes}
            onChange={(code) => changeLine(line.id, { costCodeId: code ? code.id : '', costCode: code ? code.code : '' })}
            onCreate={createAndRemember}
          />
        </TableCell>
        <TableCell className={INPUT_CELL}>
          <input type="text" aria-label="Item" value={line.description} placeholder="Item" onChange={(event) => changeLine(line.id, { description: event.target.value })} className={CELL_INPUT} />
        </TableCell>
        <TableCell className={INPUT_CELL}><NumberCell label="Qty" value={line.quantity} onCommit={(quantity) => changeLine(line.id, { quantity })} /></TableCell>
        <TableCell className={INPUT_CELL}>
          <input type="text" aria-label="Unit" value={line.unit} onChange={(event) => changeLine(line.id, { unit: event.target.value.slice(0, 12) })} className={CELL_INPUT} />
        </TableCell>
        <TableCell className={INPUT_CELL}><NumberCell label="Unit Cost" money value={line.unitCost} onCommit={(unitCost) => changeLine(line.id, { unitCost })} /></TableCell>
        {figuresCells(row, 'line-budget')}
        {pricingCells(row)}
      </TableRow>
    );
  };

  const consolidatedRows = (row: BudgetRow) => {
    const open = expanded.includes(row.id);
    const title = rowTitle(row.kind);
    const documents = row.documents ?? [];
    return (
      <React.Fragment key={row.id}>
        <TableRow className="bg-gray-100" data-testid={`row-${row.kind}`}>
          {selectCell(row)}
          <TableCell className="py-1 px-3 text-sm text-gray-700">{codeText(row)}</TableCell>
          <TableCell className="py-1 px-3 text-sm">
            <button type="button" className="flex items-center gap-1 text-left text-gray-700" aria-expanded={open} aria-label={`${title} of ${codeText(row)}: show the documents behind it`} onClick={() => toggleExpanded(row.id)} disabled={documents.length === 0}>
              {documents.length > 0 && (open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
              <span className="italic">{title}</span>
            </button>
          </TableCell>
          <TableCell className="py-1 px-3 text-right">{NOT_APPLICABLE}</TableCell>
          <TableCell className="py-1 px-3">{NOT_APPLICABLE}</TableCell>
          <TableCell className="py-1 px-3 text-right">{NOT_APPLICABLE}</TableCell>
          {figuresCells(row)}
          {row.basis > 0 ? pricingCells(row) : <><TableCell className={`${cell} text-gray-300`}>—</TableCell>{row.clientPrice > 0 ? <TableCell className={cell} title="The Client Price of its change orders">{money(row.clientPrice)}</TableCell> : <TableCell className={`${cell} text-gray-300`}>—</TableCell>}</>}
        </TableRow>
        {open && documents.map((doc) => (
          <TableRow key={`${row.id}-${doc.kind}-${doc.id}`} className="bg-gray-50 text-xs text-gray-600" data-testid="row-document">
            {selecting && <TableCell />}
            <TableCell />
            <TableCell className="py-0.5 px-3 pl-8" colSpan={4}>{doc.kind === 'order' ? 'PO' : doc.kind === 'bill' ? 'Bill' : doc.kind === 'selection' ? 'Selection' : doc.kind === 'material_order' ? 'Material Order' : 'Change Order'} · {doc.label}</TableCell>
            <TableCell className="py-0.5 px-3 text-right">{(doc.kind === 'change_order' || doc.kind === 'selection' || doc.kind === 'material_order') && doc.amount !== 0 ? money(doc.amount) : ''}</TableCell>
            <TableCell className="py-0.5 px-3 text-right">{doc.kind === 'order' ? money(doc.amount) : ''}</TableCell>
            <TableCell className="py-0.5 px-3 text-right">{doc.kind === 'bill' ? money(doc.amount) : ''}</TableCell>
            <TableCell colSpan={2} />
            <TableCell className="py-0.5 px-3 text-right">{(doc.kind === 'change_order' || doc.kind === 'selection') && doc.clientAmount ? money(doc.clientAmount) : ''}</TableCell>
          </TableRow>
        ))}
      </React.Fragment>
    );
  };

  // The rows of one child cost code sit together; there is no subtotal per child -- only the parent category rows add up
  const block = (b: BudgetCodeBlock) => (
    <React.Fragment key={b.costCodeId}>
      {b.rows.map((row) => (row.kind === 'manual' ? manualRow(row) : consolidatedRows(row)))}
    </React.Fragment>
  );

  return (
    <div className="space-y-6 w-full">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Budget</h2>
          {projectName && <p className="text-gray-600 text-sm">{projectName}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <ThreeDotMenuButton
            items={[
              { label: 'Import', icon: <Upload className="w-4 h-4" />, onClick: () => undefined },
              { label: 'Export', icon: <Download className="w-4 h-4" />, onClick: exportLines },
              selecting
                ? { label: 'Cancel selection', icon: <X className="w-4 h-4" />, onClick: leaveSelection }
                : { label: 'Select', icon: <ListChecks className="w-4 h-4" />, onClick: () => setSelecting(true) },
              { label: 'Delete', icon: <Trash2 className="w-4 h-4" />, onClick: deleteSelected, destructive: true },
            ]}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {tile('Budget', totals.budget, `${manualRows.filter(({ line }) => line && !isBlank(line)).length} lines`)}
        {tile('Committed', totals.committed, 'Purchase Orders and Bills')}
        {tile('Billed', totals.billed, 'Total Bills')}
        {tile('Remaining', totals.remaining, 'Budget − Billed', totals.remaining < 0 ? 'negative' : undefined)}
        {tile('Available to Commit', totals.available, 'Budget − Committed', totals.available < 0 ? 'negative' : undefined)}
        {tile('Client Price', totals.clientPrice, 'Total client-facing price')}
      </div>

      {/* the save message has its own row, under the KPIs */}
      <div className="flex justify-end" aria-live="polite" data-testid="save-status">{saveBadge}</div>

      {/* Status, and the Markup: its value is the default Markup (%) of NEW lines; Apply sets it on the lines that exist now */}
      <div className="flex flex-wrap items-center justify-end gap-4">
        <div className="flex items-center gap-1.5 text-sm text-gray-700">
          <span>Status</span>
          <div className="w-36" data-testid="budget-status">
            <GenericSelect
              id="budget-status"
              value={status}
              required
              onValueChange={(next) => editDoc((current) => ({ ...current, status: budgetStatusOf(next) }))}
              options={BUDGET_STATUSES.map((option) => ({ value: option.value, label: option.label }))}
            />
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-700" data-testid="general-markup">
          <span>Markup</span>
          <span className="flex h-10 w-36 items-center rounded-lg border border-gray-300 bg-white px-1">
            <NumberCell
              label="Markup"
              decimals={2}
              value={generalMarkup}
              onCommit={(value) => editDoc((current) => ({ ...current, generalMarkup: Math.max(value, -100) }))}
              onClear={() => editDoc((current) => ({ ...current, generalMarkup: null }))}
            />
          </span>
          <span>%</span>
          <StandardButton type="button" customVariant="white" onClick={applyGeneralMarkup} disabled={generalMarkup === null} title="Set this Markup on every line now (it replaces their individual markups). New lines start with it automatically.">
            Apply
          </StandardButton>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {/* The table scrolls INSIDE its box (never the page): sideways when the columns do not fit, and up / down with the header kept in view */}
          <div className="max-h-[70vh] overflow-auto" data-testid="budget-scroll">
            <Table className="min-w-[1400px]">
              <TableHeader>
                <TableRow className="bg-table-header hover:bg-table-header">
                  {selecting && (
                    <TableHead className={`${HEAD} w-8 px-2 text-center`}>
                      <Checkbox aria-label="Select all lines" ref={headerCheckboxRef} checked={allSelected} className="h-3.5 w-3.5" onCheckedChange={(value) => toggleAll(Boolean(value))} />
                    </TableHead>
                  )}
                  <TableHead className={`${HEAD} min-w-[11rem]`}>Cost Code</TableHead>
                  <TableHead className={`${HEAD} min-w-[12rem]`}>Item</TableHead>
                  <TableHead className={`${HEAD} w-20 text-right`}>Qty</TableHead>
                  <TableHead className={`${HEAD} w-24`}>Unit</TableHead>
                  <TableHead className={`${HEAD} min-w-[7.5rem] text-right`}>Unit Cost</TableHead>
                  <TableHead className={`${HEAD} min-w-[8.5rem] text-right`} title="What you estimate: Qty x Unit Cost of the manual lines. The POs / Bills lines have none">Budget</TableHead>
                  <TableHead className={`${HEAD} min-w-[8.5rem] text-right`} title="What the valid purchase orders still have open plus everything billed on this cost code (a bill an order already covers counts once; bills with no order count)">Committed</TableHead>
                  <TableHead className={`${HEAD} min-w-[8.5rem] text-right`} title="What bills have billed on this cost code. Payments are not added">Billed</TableHead>
                  <TableHead className={`${HEAD} min-w-[8.5rem] text-right`} title="Budget - Billed">Remaining</TableHead>
                  <TableHead className={`${HEAD} min-w-[6.5rem] text-right`} title="Client Price = cost x (1 + Markup / 100). Type either one: the other follows. The cost is the Budget of a manual line, the Committed of a POs / Bills line">Markup (%)</TableHead>
                  <TableHead className={`${HEAD} min-w-[8.5rem] text-right`} title="What the client is charged for the line">Client Price</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {view.sections.map((section) => {
                  const collapsed = hasGroups && collapsedSections.includes(section.key);
                  const title = section.label ?? 'No parent';
                  return (
                    <React.Fragment key={section.key || 'no-group'}>
                      {hasGroups && (
                        <TableRow className="bg-gray-200 cursor-pointer" data-testid="group-header" onClick={() => toggleSection(section.key)}>
                          <TableCell className="py-1 px-3 text-xs font-semibold uppercase tracking-wide text-gray-600" colSpan={5 + (selecting ? 1 : 0)}>
                            <button
                              type="button"
                              className="flex items-center gap-1 text-left uppercase"
                              aria-expanded={!collapsed}
                              aria-label={`${title}: ${collapsed ? 'expand' : 'collapse'} its child cost codes`}
                              onClick={(event) => { event.stopPropagation(); toggleSection(section.key); }}
                            >
                              {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                              {title}
                            </button>
                          </TableCell>
                          {figuresCells(section.total)}
                          {aggregatePricingCells(section.total)}
                        </TableRow>
                      )}
                      {!collapsed && section.blocks.map(block)}
                    </React.Fragment>
                  );
                })}
                {view.noCodeRows.map(manualRow)}
                {(view.noCode.committed !== 0 || view.noCode.billed !== 0 || view.noCode.budget !== 0 || view.noCode.clientPrice !== 0) && (
                  <TableRow className="bg-amber-50" data-testid="no-code-money">
                    {selecting && <TableCell />}
                    <TableCell className="py-1 px-3 text-sm" colSpan={5}>
                      <span className="font-medium">No cost code</span>
                      <span className="text-gray-500"> — purchase orders, bills, change orders or selections whose lines have no cost code</span>
                    </TableCell>
                    <TableCell className={`${cell} font-medium`}>{money(view.noCode.budget)}</TableCell>
                    <TableCell className={cell}>{money(view.noCode.committed)}</TableCell>
                    <TableCell className={cell}>{money(view.noCode.billed)}</TableCell>
                    <TableCell className={`${cell} ${negative(view.noCode.budget - view.noCode.billed)}`}>{money(view.noCode.budget - view.noCode.billed)}</TableCell>
                    <TableCell className={`${cell} text-gray-300`}>—</TableCell>
                    <TableCell className={cell}>{money(view.noCode.clientPrice)}</TableCell>
                  </TableRow>
                )}
                <TableRow className="bg-gray-50 font-medium">
                  {selecting && <TableCell />}
                  <TableCell className="py-1 px-3 text-sm" colSpan={5}>Totals</TableCell>
                  {figuresCells({ budget: columnBudget, committed: totals.committed, billed: totals.billed, remaining: roundCents(columnBudget - totals.billed) }, 'total-budget', true)}
                  {aggregatePricingCells({ basis: totals.basis, clientPrice: totals.clientSubtotal }, true)}
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div>
        <StandardButton onClick={addLine} customVariant="white">
          <Plus className="w-4 h-4 mr-2" />
          Add Line
        </StandardButton>
      </div>

      {/* Two independent columns. BUDGET = the cost of the job (manual lines + change orders + its own Tax and Other Amount). CLIENT PRICING = what is charged
          (the rows' Client Pricing + its own Tax and Other Amount). The markup only ever touches the second one. These Tax / Other Amount are the budget's
          own: the Tax / Other Amount of purchase orders and bills are already inside Committed and Billed. */}
      <div className="flex justify-end">
        <div className="w-full max-w-xl rounded-md border border-gray-200 bg-white px-4 py-2 text-sm" data-testid="budget-summary">
          <div className="grid grid-cols-[1fr_11rem_11rem] items-center gap-x-3 text-gray-500 text-xs font-medium pb-1">
            <span />
            <span className="text-right">Budget</span>
            <span className="text-right">Client Price</span>
          </div>
          <div className="grid grid-cols-[1fr_11rem_11rem] items-center gap-x-3 py-1">
            <span className="text-gray-600">Lines subtotal</span>
            <span className="text-right" data-testid="summary-subtotal">{money(columnBudget)}</span>
            <span className="text-right" data-testid="summary-client-subtotal">{money(totals.clientSubtotal)}</span>
          </div>
          <div className="grid grid-cols-[1fr_11rem_11rem] items-center gap-x-3 py-0.5">
            <span className="text-gray-600">Tax</span>
            <span className="flex items-center justify-end gap-2">
              <span className="flex w-20 items-center rounded border border-gray-200">
                <NumberCell label="Budget Tax (%)" decimals={2} value={taxPct} onCommit={(value) => editDoc((current) => ({ ...current, taxPct: Math.min(Math.max(value, 0), 100) }))} />
                <span className="pr-2 text-gray-500">%</span>
              </span>
              <span className="w-20 text-right" data-testid="summary-tax">{money(totals.tax)}</span>
            </span>
            <span className="flex items-center justify-end gap-2">
              <span className="flex w-20 items-center rounded border border-gray-200">
                <NumberCell label="Client Price Tax (%)" decimals={2} value={clientTaxPct} onCommit={(value) => editDoc((current) => ({ ...current, clientTaxPct: Math.min(Math.max(value, 0), 100) }))} />
                <span className="pr-2 text-gray-500">%</span>
              </span>
              <span className="w-20 text-right" data-testid="summary-client-tax">{money(totals.clientTax)}</span>
            </span>
          </div>
          <div className="grid grid-cols-[1fr_11rem_11rem] items-center gap-x-3 py-0.5">
            <span className="text-gray-600">Other Amount</span>
            <span className="flex justify-end">
              <span className="flex w-[calc(5rem+0.5rem+5rem)] items-center rounded border border-gray-200">
                <span className="pl-2 text-gray-500">$</span>
                <NumberCell label="Budget Other Amount" money value={otherAmount} onCommit={(value) => editDoc((current) => ({ ...current, otherAmount: Math.max(value, 0) }))} />
              </span>
            </span>
            <span className="flex justify-end">
              <span className="flex w-[calc(5rem+0.5rem+5rem)] items-center rounded border border-gray-200">
                <span className="pl-2 text-gray-500">$</span>
                <NumberCell label="Client Price Other Amount" money value={clientOtherAmount} onCommit={(value) => editDoc((current) => ({ ...current, clientOtherAmount: Math.max(value, 0) }))} />
              </span>
            </span>
          </div>
          <div className="grid grid-cols-[1fr_11rem_11rem] items-center gap-x-3 border-t border-gray-200 mt-1 pt-1 font-bold">
            <span>Total</span>
            <span className="text-right" data-testid="summary-total">{money(totals.budget)}</span>
            <span className="text-right" data-testid="summary-client-total">{money(totals.clientPrice)}</span>
          </div>
        </div>
      </div>

      {/* Another tab or user saved a newer version: the app's generic warning. Reload takes the newest version; Cancel keeps your unsaved changes on screen. */}
      <GenericConfirmDialog
        isOpen={conflictOpen}
        title="Newer version available"
        message="Another tab or user has a more recent version of this Budget. Reload the document before continuing."
        confirmLabel="Reload"
        cancelLabel="Cancel"
        onConfirm={() => { autosave.discard(); setReloadTick((tick) => tick + 1); }}
        onCancel={() => setConflictOpen(false)}
      />
    </div>
  );
}
